import { styled } from '@flex-design-system/stitches-react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { scaleSmallAnimation } from '../../utils/keyframes.mjs';
import { ScrollArea } from '../scroll-area/ScrollArea.mjs';
import { SlotRoot } from '../utility/layout.mjs';
import { DEFAULT_VIEWPORT_MAX_HEIGHT, LIST_PADDING_Y } from './constants.mjs';

const ITEM_GAP = 2;
const FOCUS_CLASS_NAME = "focus";
const VISUALLY_HIDDEN_DATA_ROLE = "visually-hidden";
const CONTENT_PADDING_Y = 6;
const itemStyles = {
  fontSize: 14,
  borderRadius: 6,
  clickable: "weak",
  display: "flex",
  alignItems: "flex-start",
  color: "$grayDark",
  padding: "0 8px",
  margin: "0 6px",
  position: "relative",
  userSelect: "none",
  "&[data-disabled]": {
    opacity: 0.6,
    pointerEvents: "none"
  },
  "&:focus": {
    outline: "none"
  },
  "&[hidden]": {
    display: "none"
  },
  [`&.${FOCUS_CLASS_NAME}`]: {
    background: "$blackAlpha003"
  }
};
const StyledItem = styled(DropdownMenuPrimitive.Item, {
  ...itemStyles,
  flexDirection: "column",
  variants: {
    danger: {
      true: {
        color: "$orange",
        "&:hover, &:focus": {
          backgroundColor: "$orangeLightest",
          "&:before": {
            backgroundColor: "transparent"
          }
        }
      }
    }
  }
});
const StyledItemTitle = styled("div", {
  color: "inherit",
  fontWeight: 600,
  height: 32,
  width: "100%",
  display: "flex",
  alignItems: "center"
});
const StyledItemDescription = styled("p", {
  color: "$blackAlpha064",
  fontSize: "12px",
  minHeight: "24px",
  lineHeight: 1.5,
  paddingBottom: 6
});
const StyledRadioGroup = styled(DropdownMenuPrimitive.RadioGroup);
StyledRadioGroup.toString = () => "fx-dropdown-radio-group";
const StyledGroup = styled(DropdownMenuPrimitive.Group);
StyledGroup.toString = () => "fx-dropdown-group";
const contentStyles = {
  overflow: "hidden",
  backgroundColor: "$white",
  borderRadius: 10,
  boxShadow: "$popper",
  position: "relative",
  "@media (prefers-reduced-motion: no-preference)": {
    animationDuration: scaleSmallAnimation.duration.open,
    animationTimingFunction: scaleSmallAnimation.timingFunction,
    willChange: "transform, opacity",
    '&[data-align="start"]': {
      '&[data-side="top"]': { transformOrigin: "20% 80%" },
      '&[data-side="bottom"]': { transformOrigin: "20% 20%" }
    },
    '&[data-align="center"]': {
      '&[data-side="top"]': { transformOrigin: "50% 80%" },
      '&[data-side="bottom"]': { transformOrigin: "50% 20%" }
    },
    '&[data-align="end"]': {
      '&[data-side="top"]': { transformOrigin: "80% 80%" },
      '&[data-side="bottom"]': { transformOrigin: "80% 20%" }
    },
    '&[data-state="open"]': {
      animationName: scaleSmallAnimation.show(0.98)
    },
    '&[data-state="closed"]': {
      animationDuration: scaleSmallAnimation.duration.close,
      animationName: scaleSmallAnimation.hide(0.99)
    }
  }
};
const StyledContent = styled(DropdownMenuPrimitive.Content, {
  ...contentStyles,
  // aside까지 포함하는 용도
  display: "flex",
  variants: {
    /**
     * @description - props로 animation 사용 여부를 결정 할 수 있도록 합니다.
     */
    prefersReducedMotion: {
      noPreference: {
        // Nothing
      },
      reduce: {
        "&&": {
          animationDuration: 0,
          animationName: "none"
        }
      }
    }
  }
});
const StyledContentRoot = styled("div", {
  display: "flex",
  flexDirection: "column"
});
const StyledContentRootScrollArea = styled(ScrollArea, {
  flexGrow: 1
});
const StyledSubContent = styled(DropdownMenuPrimitive.SubContent, {
  ...contentStyles,
  maxHeight: `min(${DEFAULT_VIEWPORT_MAX_HEIGHT}px, calc(100vh - 24px))`,
  // https://linear.app/flexteam/issue/FX-144/dropdown-submenu-animation-제거
  "&&": {
    animationDuration: 0,
    animationName: "none"
  }
});
const StyledAsideRoot = styled("aside", {
  display: "flex",
  flexDirection: "column",
  "&&&": {
    // DropdownContent에서 지정하는 margin제거
    marginTop: 0
  },
  background: "$background",
  variants: {
    side: {
      left: {
        boxShadow: "inset -1px 0px 0px rgb(0 0 0 / 8%)"
      },
      right: {
        boxShadow: "inset 1px 0px 0px rgb(0 0 0 / 8%)"
      }
    }
  }
});
const StyledAsideAffixRoot = styled("div", {
  minHeight: 45,
  px: 14,
  display: "flex",
  alignItems: "center"
});
const StyledViewport = styled(ScrollArea.Viewport_Legacy, {
  maxHeight: DEFAULT_VIEWPORT_MAX_HEIGHT,
  minWidth: 200,
  paddingBottom: LIST_PADDING_Y,
  py: CONTENT_PADDING_Y,
  [`> div > *:not([data-role=${VISUALLY_HIDDEN_DATA_ROLE}])`]: {
    marginTop: ITEM_GAP
  },
  "> div > *:first-child": {
    marginTop: "unset"
  },
  [`> div > [role="group"] > *:not([data-role=${VISUALLY_HIDDEN_DATA_ROLE}])`]: {
    marginTop: ITEM_GAP
  },
  '> div > [role="group"] > *:first-child': {
    marginTop: "unset"
  }
});
const StyledCheckboxItem = styled(DropdownMenuPrimitive.CheckboxItem, {
  ...itemStyles,
  color: "$grayDark",
  flexDirection: "column"
});
const StyledRadioItem = styled(DropdownMenuPrimitive.RadioItem, {
  ...itemStyles
});
const StyledSubTrigger = styled(DropdownMenuPrimitive.SubTrigger, {
  ...itemStyles,
  '&[data-state="open"]': {
    backgroundColor: "$blackAlpha003",
    "&:before": {
      display: "none"
    }
  }
});
const StyledLabel = styled(DropdownMenuPrimitive.Label, {
  borderRadius: 6,
  display: "flex",
  alignItems: "center",
  padding: "0 14px 0 14px",
  color: "$blackAlpha040",
  fontWeight: 600,
  fontSize: "12px",
  height: "28px"
});
const StyledSeparatorRoot = styled("div", {
  height: 9,
  display: "flex",
  alignItems: "center"
});
const StyledSeparator = styled("div", {
  height: 1,
  width: "100%",
  backgroundColor: "$blackAlpha006"
});
const LeftSlotRoot = styled(SlotRoot, {
  height: "22px",
  "& > svg": {
    width: "22px"
  }
});
const StyledAffixRoot = styled("div", {
  py: CONTENT_PADDING_Y,
  display: "grid",
  gridAutoFlow: "row",
  gridAutoColumns: "1fr",
  gap: 6,
  borderBottom: "1px solid rgba(36, 42, 48, 0.06)"
});
const StyledContentFooterRoot = styled("div", {
  py: CONTENT_PADDING_Y,
  display: "grid",
  gridAutoFlow: "row",
  gridAutoColumns: "1fr",
  gap: 6,
  borderTop: "1px solid rgba(36, 42, 48, 0.06)"
});
const WithoutPortal = styled("div", {
  '& [data-scope="dropdown"][data-part="content-root"]': { width: "100%" },
  "& > div": {
    all: "unset !important",
    "& > div": {
      "pointer-events": "auto !important"
    }
  }
});

export { FOCUS_CLASS_NAME, ITEM_GAP, LeftSlotRoot, StyledAffixRoot, StyledAsideAffixRoot, StyledAsideRoot, StyledCheckboxItem, StyledContent, StyledContentFooterRoot, StyledContentRoot, StyledContentRootScrollArea, StyledGroup, StyledItem, StyledItemDescription, StyledItemTitle, StyledLabel, StyledRadioGroup, StyledRadioItem, StyledSeparator, StyledSeparatorRoot, StyledSubContent, StyledSubTrigger, StyledViewport, WithoutPortal };
//# sourceMappingURL=styled.mjs.map
